/**
 * RELEASES COMPARISON TAB (project scope only)
 *
 * Side-by-side view of every release in the project. Reuses the existing
 * `fetchAllReleasesReportAsync` thunk + `releasesSummary` Redux slot —
 * project-level Overview already populates it, so the tab usually renders
 * instantly when switched to.
 *
 * Two views on the same data:
 *   1. Composed chart — grouped bars for executed/not-executed + line for pass rate.
 *   2. Comparison table — one row per release with progress bar + pass rate.
 */

import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Spinner from '@atlaskit/spinner'
import Lozenge from '@atlaskit/lozenge'
import { token } from '@atlaskit/tokens'

import ReleaseComparisonChart from '../charts/ReleaseComparisonChart'
import StatusProgressBar from '../charts/StatusProgressBar'
import {
  fetchAllReleasesReportAsync,
  selectReleasesSummary,
  selectReportLoading,
  selectReportErrors
} from '../../../store/slices/reportsSlice'

const STATUS_APPEARANCE = {
  Draft: 'default',
  Active: 'inprogress',
  Released: 'success',
  Archived: 'moved'
}

const thStyle = {
  padding: '10px 12px',
  textAlign: 'left',
  fontWeight: 500,
  color: token('color.text.subtle', '#6B778C'),
  borderBottom: `1px solid ${token('color.border', '#DFE1E6')}`,
  fontSize: '12px',
  textTransform: 'uppercase',
  letterSpacing: '0.3px'
}

const tdStyle = {
  padding: '10px 12px',
  color: token('color.text', '#172B4D'),
  fontSize: '13px',
  borderBottom: `1px solid ${token('color.border', '#DFE1E6')}`
}

const rowBg = (index) =>
  index % 2 === 0
    ? token('elevation.surface', '#FFFFFF')
    : token('color.background.neutral.subtle', '#FAFBFC')

const passRateColor = (rate) => {
  if (rate >= 80) return token('color.text.success', '#22A06B')
  if (rate >= 50) return token('color.text.warning', '#FF991F')
  return token('color.text.danger', '#DE350B')
}

const ReleasesComparisonTab = ({ projectId }) => {
  const dispatch = useDispatch()
  const releases = useSelector(selectReleasesSummary)
  const loading = useSelector(selectReportLoading)
  const errors = useSelector(selectReportErrors)

  useEffect(() => {
    if (projectId && (!releases || releases.length === 0)) {
      dispatch(fetchAllReleasesReportAsync({ projectId }))
    }
  }, [dispatch, projectId, releases])

  if (loading.releasesSummary) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
        <Spinner size="large" />
      </div>
    )
  }

  if (errors.releasesSummary) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: token('color.text.danger', '#DE350B') }}>
        <p>Failed to load releases comparison</p>
        <p style={{ fontSize: '12px', color: token('color.text.subtle', '#6B778C') }}>{errors.releasesSummary}</p>
      </div>
    )
  }

  const rows = releases || []

  if (rows.length === 0) {
    return (
      <div
        style={{
          padding: '32px',
          textAlign: 'center',
          color: token('color.text.subtle', '#6B778C'),
          fontSize: '13px',
          border: `1px dashed ${token('color.border', '#DFE1E6')}`,
          borderRadius: '4px'
        }}
      >
        No releases in this project yet.
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <section>
        <div
          style={{
            fontSize: '14px',
            fontWeight: 600,
            color: token('color.text', '#172B4D'),
            marginBottom: '8px'
          }}
        >
          Executed vs not-executed, and pass rate
        </div>
        <ReleaseComparisonChart releases={rows} height={300} />
      </section>

      <section>
        <div
          style={{
            border: `1px solid ${token('color.border', '#DFE1E6')}`,
            borderRadius: '4px',
            overflow: 'hidden'
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: token('color.background.neutral', '#F4F5F7') }}>
                <th style={thStyle}>Release</th>
                <th style={thStyle}>Status</th>
                <th style={{ ...thStyle, width: '180px' }}>Progress</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Pass rate</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Test cases</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((release, idx) => (
                <tr key={release.releaseId} style={{ backgroundColor: rowBg(idx) }}>
                  <td style={tdStyle}>
                    {release.releaseName}
                    {release.archived && (
                      <span
                        style={{
                          marginLeft: '8px',
                          fontSize: '11px',
                          color: token('color.text.subtle', '#6B778C')
                        }}
                      >
                        (Archived)
                      </span>
                    )}
                  </td>
                  <td style={tdStyle}>
                    <Lozenge appearance={STATUS_APPEARANCE[release.releaseStatus] || 'default'}>
                      {release.releaseStatus}
                    </Lozenge>
                  </td>
                  <td style={tdStyle}>
                    <StatusProgressBar
                      statusDistribution={release.summary?.statusDistribution}
                      height={8}
                    />
                  </td>
                  <td
                    style={{
                      ...tdStyle,
                      textAlign: 'right',
                      fontWeight: 600,
                      color: passRateColor(release.summary?.passRate || 0)
                    }}
                  >
                    {Number(release.summary?.passRate || 0).toFixed(1)}%
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: token('color.text.subtle', '#6B778C') }}>
                    {release.summary?.totalTestCases || 0}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}

export default ReleasesComparisonTab
